/**
 * Common ShowcaseProductRail component used by other modules as a reusable component which returns the ui for a showcaseunit product carousel.
 *
 * @module views/Organisms/ShowcaseProductRail
 * @memberof -Common
 */
import './ShowcaseProductRail.scss';

import React, { useRef } from 'react';

import PropTypes from 'prop-types';

import AsyncComponent from '@ulta/core/components/AsyncComponent/AsyncComponent';
import GridContainer from '@ulta/core/components/GridContainer/GridContainer';
import Rail, { Slide } from '@ulta/core/components/Rail/Rail';
import Text from '@ulta/core/components/Text/Text';
import { useDeviceInflection } from '@ulta/core/providers/InflectionProvider/InflectionProvider';
import { hasItems } from '@ulta/core/utils/array/array';
import { spreadProps } from '@ulta/core/utils/dom/dom';

import ShowcaseMedia from '@ulta/components/ShowcaseMedia/ShowcaseMedia';

import { useUuid } from '@ulta/hooks/useUuid/useUuid';

import carouselUtils from '@ulta/utils/carouselUtils/carouselUtils';

/**
 * Represents a ShowcaseProductRail component
 *
 * @method
 * @param {ShowcaseProductRailProps} props - React properties passed from composition
 * @returns ShowcaseProductRail
 */
const ShowcaseProductRail = ( props ) => {
  const productRailHorizontalCompactRef = useRef( null );

  const { breakpoint } = useDeviceInflection();

  const {
    componentKey,
    dataCapture,
    items = defaultProps.items,
    showcaseMedia,

    /* Rail Title */
    countLabel = defaultProps.countLabel,
    title = defaultProps.title,

    /* Rail meta data */
    carouselAccessibility,
    carouselCardAccessibility,
    carouselDetailsAccessibility,
    listPriceAccessibilityLabel,
    listPriceRangeAccessibilityLabel,
    nextAccessibility = defaultProps.nextAccessibility,
    nextClickAction,
    previousAccessibility = defaultProps.previousAccessibility,
    previousClickAction,
    reviewAccessibilityLabel,
    salePriceAccessibilityLabel,
    salePriceRangeAccessibilityLabel
  } = props;

  /* Setting variables */
  const showCountLabel = items?.length > 1;

  /* Sets the UUID for the rail buttons */
  const [railUuid] = useUuid();

  /* Checks if Rail is in a small device */
  const isSmallDevice = breakpoint.isSmallDevice();

  /* Interpolating accesibility strings */
  const newCarouselAccessibility = carouselUtils.handleTokenizedString( carouselAccessibility, [title] );
  const newCarouselCardAccessibility = carouselUtils.handleTokenizedString( carouselCardAccessibility, [title] );
  const newCarouselDetailsAccessibility = carouselUtils.handleTokenizedString( carouselDetailsAccessibility, [title] );

  /* ADA Objects */
  const _railADAmeta = {
    carouselAccessibility: newCarouselAccessibility,
    carouselCardAccessibility: newCarouselCardAccessibility,
    carouselDetailsAccessibility: newCarouselDetailsAccessibility
  };

  const _railMeta = {
    countLabel: countLabel,
    nextAccessibility: nextAccessibility,
    previousAccessibility: previousAccessibility
  };

  const isShowcaseMedia = !!(
    showcaseMedia &&
    showcaseMedia?.mediaAction
  );

  if( !hasItems( items ) || !isShowcaseMedia ){
    return null;
  }

  return (
    <div
      className='ShowcaseProductRail'
      ref={ productRailHorizontalCompactRef }
    >
      <GridContainer>
        <div className='ShowcaseProductRail__header'>
          <Text
            textStyle='title-6'
            htmlTag='h2'
          >
            { title }
          </Text>

        </div>
        { !isSmallDevice && showCountLabel &&
          <div className='ShowcaseProductRail__countLabel'>
            <Text
              textStyle='body-2'
              htmlTag='p'
              color='neutral-600'
            >
              { countLabel }
            </Text>
          </div>
        }
        <div className='ShowcaseProductRail__media'>
          <ShowcaseMedia
            { ...spreadProps( { ...showcaseMedia } ) }
            title={ title }
          />
        </div>
        { isSmallDevice && showCountLabel &&
          <div className='ShowcaseProductRail__countLabel'>
            <Text textStyle='body-2'
              htmlTag='p'
              color='neutral-600'
            >
              { countLabel }
            </Text>
          </div>
        }
        <div
          className='ShowcaseProductRail__carousel'
          role='region'
        >
          <Rail
            railRef={ productRailHorizontalCompactRef }
            id={ railUuid }
            items={ items }
            previousClickAction={ previousClickAction }
            nextClickAction={ nextClickAction }
            dataCapture={ dataCapture }
            _railADAmeta={ _railADAmeta }
            _railMeta={ _railMeta }
            sliderPerViewXL={ 2 }
            slidesPerGroupXL={ 2 }
            sliderPerViewLG={ 2 }
            slidesPerGroupLG={ 2 }
            sliderPerViewMD={ 1 }
            slidesPerGroupMD={ 1 }
            slidesPerGroupSM={ 1 }
            sliderPerViewSM={ 1.5 }
          >
            { items?.length > 0 &&
                items.map( ( product, index ) => {
                  return (
                    <Slide key={ index }>
                      <div key={ product.skuId + '_' + index }>
                        <AsyncComponent
                          moduleName='ProductCard'
                          carouselIndex={ index }
                          componentKey={ componentKey }
                          // TODO: this componentLastUpdated needs to be refactored -- https://ulta.atlassian.net/browse/DSOTF2-109266
                          componentLastUpdated={ componentKey }
                          skuId={ product.skuId }
                          productId={ product.productId }
                          action={ product.action }
                          additionalOffersText={ product.additionalOffersText }
                          altImage={ product.altImage }
                          badge={ product.badge }
                          sponsoredBadgeLabel={ product.sponsoredBadgeLabel }
                          bookmarkAccessibility={ product.bookmarkAccessibility }
                          bookmarked={ product.bookmarked }
                          brandName={ product.brandName }
                          image={ product.image?.imageUrl }
                          kitPrice={ product.kitPrice }
                          listPrice={ product.listPrice }
                          productName={ product.productName }
                          promoText={ product.promoText }
                          rating={ product.rating }
                          reviewCount={ product.reviewCount }
                          salePrice={ product.salePrice }
                          variantLabel={ product.variantLabel }
                          hoverState={ false }
                          displayBookmark={ false }
                          addToBagAction={ product.addToBagAction }
                          viewOptionAction={ product.viewOptionAction }
                          availability={ product.availability }
                          salePriceAccessibilityLabel={ salePriceAccessibilityLabel }
                          listPriceAccessibilityLabel={ listPriceAccessibilityLabel }
                          salePriceRangeAccessibilityLabel={ salePriceRangeAccessibilityLabel }
                          listPriceRangeAccessibilityLabel={ listPriceRangeAccessibilityLabel }
                          { ...spreadProps( {
                            reviewAccessibilityLabel: reviewAccessibilityLabel
                          } ) }
                        />
                      </div>
                    </Slide>
                  );
                } )
            }
          </Rail>
        </div>
      </GridContainer>
    </div>
  );
};

/**
 * Default values for passed properties
 * @type {object}
 * @property {string} countLabel='' - The default value of the count label is empty string.
 * @property {array} items=[] - The default value for items is set to empty array.
 * @property {string} nextAccessibility='' - The default value of the nextAccessibility is empty string.
 * @property {string} previousAccessibility='' - The default value of the previousAccessibility is empty string.
 * @property {string} title='' - The default value of the title is empty string.
 */
export const defaultProps = {
  countLabel: '',
  items: [],
  nextAccessibility: '',
  previousAccessibility: '',
  title: ''
};

/**
 * Property type definitions
 * @typedef ProductRailHorizontalProps
 * @type {object}
 * @property {string} carouselAccessibility - Value for aria label for product rail
 * @property {string} carouselCardAccessibility - Value for the product card aria label
 * @property {string} carouselDetailsAccessibility - Value for aria label for carousel
 * @property {number} componentKey - Sets the component key
 * @property {string} countLabel - Sets the count label for productRail list
 * @property {Array.<{promoText: string, reviewCount: Number, rating: Number, brandName: string, productName: string, variantLabel: string, badge: string, image: string, availability: array, addToBagAccessibility: string, additionalOffersText: string, altImage: string, bookmarkAccessibility: string, bookmarked: boolean, kitPrice: string, listPrice: string, salePrice: string, skuId: string}>} items - List of products to loop over and display in carousel
 * @property {string} listPriceAccessibilityLabel - Sets the list price accessibility label
 * @property {string} listPriceRangeAccessibilityLabel - Sets the list price range accessibility label
 * @property {string} nextAccessibility - Sets the aria label for next button
 * @property {object} nextClickAction - This is the action object for the Previous button
 * @property {string} previousAccessibility - Sets the aria label for previous button
 * @property {object} previousClickAction - This is the action object for the Next button
 * @property {string} reviewAccessibilityLabel - Value for the rating aria label
 * @property {string} salePriceAccessibilityLabel - Sets the sale price accessibility label
 * @property {string} salePriceRangeAccessibilityLabel - Sets the sale price range accessibility label
 * @property {object} showcaseMedia - This is the object for the showcaseMedia
 * @property {string} title - Sets the title for productRail list
 */
export const propTypes = {
  /**
   * The prop that holds  aria label for carousel
   */
  carouselAccessibility: PropTypes.string,
  /**
   * The prop that holds  aria label for product card
   */
  carouselCardAccessibility: PropTypes.string,
  /**
   * The prop that holds  aria label for carousel
   */
  carouselDetailsAccessibility: PropTypes.string,
  /**
   * Sets the component key
  */
  componentKey: PropTypes.number,
  /**
  * The prop that holds the count label for the product list.
  */
  countLabel: PropTypes.string,
  /**
  * The prop that holds the list of products to render.
  */
  items: PropTypes.arrayOf(
    PropTypes.shape( {
      promoText: PropTypes.string,
      reviewCount: PropTypes.number,
      rating: PropTypes.number,
      brandName: PropTypes.string,
      productName: PropTypes.string,
      variantLabel: PropTypes.string,
      badge: PropTypes.string,
      image: PropTypes.object,
      availability: PropTypes.arrayOf( PropTypes.string ),
      listPrice: PropTypes.string,
      salePrice: PropTypes.string,
      kitPrice: PropTypes.string,
      bookmarkAccessibility: PropTypes.string,
      addToBagAccessibility: PropTypes.string,
      additionalOffersText: PropTypes.string,
      altImage: PropTypes.string,
      bookmarked: PropTypes.bool,
      skuId: PropTypes.string
    } )
  ),
  /**
  * Sets the list price accessibility label
  */
  listPriceAccessibilityLabel: PropTypes.string,
  /**
  * Sets the list price range accessibility label
  */
  listPriceRangeAccessibilityLabel: PropTypes.string,
  /**
  * The prop that holds the aria label for next button.
  */
  nextAccessibility: PropTypes.string,
  /**
   * The prop that holds the action for the next button.
   */
  nextClickAction: PropTypes.object,
  /**
  * The prop that holds the aria label for previous button.
  */
  previousAccessibility: PropTypes.string,
  /**
   * The prop that holds the action for the previous button.
   */
  previousClickAction: PropTypes.object,
  /**
  * The prop that decides to show aria-label.
  */
  reviewAccessibilityLabel: PropTypes.string,
  /**
  * Sets the sale price accessibility label
  */
  salePriceAccessibilityLabel: PropTypes.string,
  /**
  * Sets the sale price range accessibility label
  */
  salePriceRangeAccessibilityLabel: PropTypes.string,
  /**
   * This is the object for the showcaseMedia
   */
  showcaseMedia: PropTypes.object,
  /**
  * The prop that holds title for the carousel.
  */
  title: PropTypes.string
};

ShowcaseProductRail.propTypes = propTypes;
ShowcaseProductRail.defaultProps = defaultProps;

export default ShowcaseProductRail;